<div class="min-h-screen p-4 lg:p-6 bg-white dark:bg-neutral-900">
  <!-- Header -->
  <div class="flex justify-between items-center mb-6 flex-col gap-4 md:flex-row">
    <div class="flex items-center gap-4 w-full md:w-auto">
      <!-- Document Icon -->
      <div class="relative">
        <div
          class="w-20 h-20 rounded-full bg-neutral-100 dark:bg-neutral-700 border-2 border-neutral-200 dark:border-neutral-600 flex items-center justify-center"
        >
          <i class="fa fa-light fa-file-text text-neutral-500 dark:text-neutral-400 text-2xl"></i>
        </div>
      </div>

      <!-- Title and Status -->
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          {{
            isEditMode
              ? kbForm.get('title')?.value || ('kbForm.title.edit' | transloco)
              : ('kbForm.title.new' | transloco)
          }}
        </h1>
        <span class="text-sm text-neutral-500 dark:text-neutral-400">{{
          'kbForm.status.draft' | transloco
        }}</span>
      </div>
    </div>
    <div class="flex gap-3 w-full md:w-auto justify-end">
      <p-button
        [label]="
          isEditMode
            ? ('kbForm.buttons.saveChanges' | transloco)
            : ('kbForm.buttons.createDocument' | transloco)
        "
        size="small"
        (onClick)="onSubmit()"
        [disabled]="isSaveDisabled"
        [loading]="isSubmitting"
      >
      </p-button>
      <p-button
        [label]="'kbForm.buttons.cancel' | transloco"
        severity="secondary"
        size="small"
        (onClick)="onCancel()"
        [outlined]="true"
        [disabled]="isSubmitting"
      >
      </p-button>
    </div>
  </div>

  <!-- Form -->
  <form [formGroup]="kbForm" class="space-y-6">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
      <!-- Left Column -->
      <div class="space-y-6">
        <!-- File Upload -->
        <div
          class="rounded-md border border-neutral-300 dark:border-neutral-700/30 p-4 lg:p-6 bg-white dark:bg-neutral-800/30"
        >
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            {{ 'kbForm.sections.fileUpload' | transloco }}
          </h2>

          <!-- File Selection -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {{ 'kbForm.fields.file' | transloco }}
            </label>
            <small class="text-neutral-500 dark:text-neutral-400 text-xs mb-3 block">
              {{ 'kbForm.help.fileUpload' | transloco }}<br />
              <strong>{{ 'kbForm.help.supportedFormats' | transloco }}:</strong>
              {{ 'kbForm.help.supportedFormatsList' | transloco }}
            </small>

            <!-- File Input -->
            <div class="space-y-3">
              <input
                type="file"
                #fileInput
                accept=".pdf,.txt,.md,.doc,.docx,.csv"
                (change)="onFileSelect($event)"
                class="hidden"
                id="fileInput"
              />
              <label
                for="fileInput"
                class="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md cursor-pointer transition-colors duration-200"
              >
                <i class="pi pi-upload"></i>
                {{ 'kbForm.buttons.selectFile' | transloco }}
              </label>
            </div>

            <!-- Original File Display (Edit Mode) -->
            @if (isEditMode && originalFile && !selectedFile) {
              <div
                class="mt-3 p-3 bg-neutral-50 dark:bg-neutral-700 rounded-md border border-neutral-200 dark:border-neutral-600"
              >
                <div class="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-2">
                  <div class="flex items-center gap-2 flex-1 min-w-0 w-full">
                    <i class="pi pi-file text-green-500 flex-shrink-0"></i>
                    <span
                      class="text-sm text-neutral-700 dark:text-neutral-300 truncate"
                      [title]="originalFile"
                    >
                      {{ originalFile.split('/').pop() || originalFile }}
                    </span>
                    <span class="text-xs text-neutral-500 dark:text-neutral-400 flex-shrink-0">
                      ({{ 'kbForm.fileStatus.current' | transloco }})
                    </span>
                  </div>
                  <button
                    type="button"
                    (click)="removeFile()"
                    class="ms-2 p-1 text-neutral-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400 transition-colors duration-200"
                    [title]="'kbForm.buttons.removeFile' | transloco"
                  >
                    <i class="pi pi-times text-sm"></i>
                  </button>
                </div>
              </div>
            }

            <!-- New File Display (Changed File) -->
            @if (selectedFile) {
              <div
                class="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700"
              >
                <div class="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-2">
                  <div class="flex items-center gap-2 flex-1 min-w-0 w-full">
                    <i class="pi pi-file text-blue-500 flex-shrink-0"></i>
                    <span
                      class="text-sm text-neutral-700 dark:text-neutral-300 truncate"
                      [title]="selectedFile.name"
                    >
                      {{ selectedFile.name }}
                    </span>
                    <span class="text-xs text-neutral-500 dark:text-neutral-400 flex-shrink-0">
                      ({{ formatFileSize(selectedFile.size) }})
                    </span>
                    @if (isEditMode && hasFileChanged()) {
                      <span
                        class="text-xs bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full"
                      >
                        {{ 'kbForm.fileStatus.new' | transloco }}
                      </span>
                    }
                  </div>
                  <button
                    type="button"
                    (click)="removeFile()"
                    class="ms-2 p-1 text-neutral-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400 transition-colors duration-200"
                    [title]="'kbForm.buttons.removeFile' | transloco"
                  >
                    <i class="pi pi-times text-sm"></i>
                  </button>
                </div>
              </div>
            }
          </div>
        </div>

        <!-- General Information -->
        <div
          class="rounded-md border border-neutral-300 dark:border-neutral-700/30 p-4 lg:p-6 bg-white dark:bg-neutral-800/30"
        >
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            {{ 'kbForm.sections.generalInfo' | transloco }}
          </h2>

          <!-- Title -->
          <div class="mb-4">
            <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              {{ 'kbForm.fields.title' | transloco }}
              <span class="text-red-700 dark:text-red-400">*</span>
            </label>
            <input
              pInputText
              formControlName="title"
              [placeholder]="'kbForm.placeholders.title' | transloco"
              class="w-full"
              [class.ng-invalid]="kbForm.get('title')?.invalid && kbForm.get('title')?.touched"
            />
            @if (kbForm.get('title')?.invalid && kbForm.get('title')?.touched) {
              <p-message
                [severity]="'error'"
                [text]="'kbForm.validation.titleRequired' | transloco"
              >
              </p-message>
            }
          </div>

          <!-- Is Sensitive -->
          <div class="mb-4">
            <div class="flex items-center gap-2">
              <p-checkbox
                formControlName="is_sensitive"
                [binary]="true"
                inputId="is_sensitive"
              />
              <label for="is_sensitive" class="text-sm font-medium text-neutral-700 dark:text-neutral-300 cursor-pointer">
                {{ 'kbForm.fields.isSensitive' | transloco }}
              </label>
            </div>
            <small class="text-neutral-500 dark:text-neutral-400 text-xs mt-1 block ms-6">
              {{ 'kbForm.help.isSensitive' | transloco }}
            </small>
          </div>

          <!-- Description -->
          <!-- <div class="mb-4">
           <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
             {{ 'kbForm.fields.description' | transloco }}
           </label>
           <textarea pInputText formControlName="description"
             [placeholder]="'kbForm.placeholders.description' | transloco" rows="4" class="w-full"
             style="resize: vertical;">
           </textarea>
           <small class="text-neutral-500 dark:text-neutral-400 text-xs mt-1 block">
             {{ 'kbForm.help.description' | transloco }}
           </small>
         </div> -->

          <!-- Document Type -->
          <!-- <div class="mb-4">
           <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
             {{ 'kbForm.fields.type' | transloco }} <span class="text-red-700 dark:text-red-400">*</span>
           </label>
           <p-select [options]="documentTypeOptions" formControlName="type"
             [placeholder]="'kbForm.placeholders.type' | transloco" class="w-full"
             [class.ng-invalid]="kbForm.get('type')?.invalid && kbForm.get('type')?.touched">
             <ng-template pTemplate="item" let-option>
               <span>{{ option.label | transloco }}</span>
             </ng-template>
             <ng-template pTemplate="selectedItem" let-option>
               <span>{{ option.label | transloco }}</span>
             </ng-template>
           </p-select>

           @if (kbForm.get('type')?.invalid && kbForm.get('type')?.touched) {
           <p-message [severity]="'error'" [text]="'kbForm.validation.typeRequired' | transloco">
           </p-message>
           }
         </div> -->

          <!-- Tags -->
          <!-- <div class="mb-4">
           <label class="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
             {{ 'kbForm.fields.tags' | transloco }}
           </label>
           <small class="text-neutral-500 dark:text-neutral-400 text-xs mb-2 block">
             {{ 'kbForm.help.tags' | transloco }}
           </small> -->

          <!-- Selected Tags as Chips -->
          <!-- @if (selectedTags.length > 0) {
           <div class="flex flex-wrap gap-2 mb-3">
             @for (tag of selectedTags; track $index) {
             <span class="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border"
               [style.background-color]="tag.color + '20'" [style.border-color]="tag.color + '40'"
               [style.color]="tag.color">
               {{ tag.name }}
               <button type="button" (click)="removeTag(tag)"
                 class="ms-1 text-neutral-400 hover:text-red-500 dark:text-neutral-500 dark:hover:text-red-400">
                 <i class="pi pi-times text-xs"></i>
               </button>
             </span>
             }
           </div>
           }

           <p-autoComplete [suggestions]="filteredTags" (completeMethod)="filterTags($event)"
             (onSelect)="onTagSelect($event)" formControlName="tagInputControl" [dropdown]="true" optionLabel="name"
             (onKeyUp)="addTag($event)" [placeholder]="'kbForm.placeholders.tags' | transloco" [forceSelection]="false"
             class="w-full" fluid>
           </p-autoComplete>
           <small>{{ 'kbForm.help.tagsEnter' | transloco }}</small>
         </div> -->
        </div>
      </div>

      <!-- Right Column -->
      <div class="space-y-6">
        <!-- Additional Information -->
        <div
          class="rounded-md border border-neutral-300 dark:border-neutral-700/30 p-4 lg:p-6 bg-white dark:bg-neutral-800/30"
        >
          <h2 class="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            {{ 'kbForm.sections.additionalInfo' | transloco }}
          </h2>

          <!-- Tips -->
          <div class="space-y-3">
            <div
              class="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-200"
            >
              <strong>💡 {{ 'kbForm.tips.organization' | transloco }}:</strong>
              {{ 'kbForm.tips.organizationText' | transloco }}
            </div>
            <div
              class="p-3 bg-green-50 border border-green-200 rounded text-xs text-green-800 dark:bg-green-900 dark:border-green-800 dark:text-green-200"
            >
              <strong>📁 {{ 'kbForm.tips.fileTypes' | transloco }}:</strong>
              {{ 'kbForm.tips.fileTypesText' | transloco }}
            </div>
            <div
              class="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 dark:bg-amber-900 dark:border-amber-800 dark:text-amber-200"
            >
              <strong>🏷️ {{ 'kbForm.tips.tags' | transloco }}:</strong>
              {{ 'kbForm.tips.tagsText' | transloco }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</div>
